import axios, { AxiosAdapter, AxiosRequestConfig, AxiosResponse } from "axios";

const inFlightRequests = new Map<string, Promise<AxiosResponse>>();

const stableStringify = (value: unknown): string => {
  if (!value || typeof value !== "object") {
    return JSON.stringify(value);
  }

  if (value instanceof URLSearchParams) {
    return value.toString();
  }

  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(",")}]`;
  }

  return `{${Object.entries(value as Record<string, unknown>)
    .sort(([firstKey], [secondKey]) => firstKey.localeCompare(secondKey))
    .map(([key, entryValue]) => `${key}:${stableStringify(entryValue)}`)
    .join(",")}}`;
};

const getRequestKey = (config: AxiosRequestConfig) => {
  const method = (config.method || "get").toLowerCase();
  const baseURL = config.baseURL || "";
  const url = config.url || "";
  const params = stableStringify(config.params);

  return `${method}:${baseURL}:${url}:${params}`;
};

export const createInFlightRequestDedupAdapter = (): AxiosAdapter => {
  const defaultAdapter = axios.getAdapter(axios.defaults.adapter);

  return async (config) => {
    const method = (config.method || "get").toLowerCase();

    if (method !== "get") {
      return defaultAdapter(config);
    }

    const requestKey = getRequestKey(config);
    const existingRequest = inFlightRequests.get(requestKey);

    if (existingRequest) {
      return existingRequest;
    }

    const request = defaultAdapter(config).finally(() => {
      inFlightRequests.delete(requestKey);
    });

    inFlightRequests.set(requestKey, request);

    return request;
  };
};
